/**
 * One definition of "signed out" for everything that ends a session.
 *
 * Sign-in state lives in TWO places in localStorage:
 *   - `auth_token` / `refresh_token` — what every request actually sends
 *     (api/client.ts request interceptor);
 *   - `auth-storage` — the zustand persist copy (`isAuthenticated`, `token`,
 *     `user`) that the route guards read.
 *
 * The 401 handler used to delete only the first pair and reload to /welcome.
 * The reload rehydrated `isAuthenticated: true` from the second, /welcome
 * redirected to the dashboard, the dashboard's /auth/me got 401 with the dead
 * token, the handler reloaded to /welcome… forever, until someone cleared
 * localStorage by hand. Clearing both here, together, is what ends the loop.
 *
 * No store import: api/client.ts (which the store imports) calls this.
 */

export const AUTH_STORE_KEY = 'auth-storage';

/** Forget the session everywhere it is persisted. Never throws. */
export function clearStoredSession(storage: Pick<Storage, 'removeItem'> = localStorage): void {
  for (const key of ['auth_token', 'refresh_token', AUTH_STORE_KEY]) {
    try {
      storage.removeItem(key);
    } catch {
      // Storage disabled / quota — nothing to clear, nothing to loop on.
    }
  }
}

interface PersistedAuth {
  token?: string | null;
  user?: unknown;
  isAuthenticated?: boolean;
}

/**
 * What the auth store may restore on boot.
 *
 * "Signed in" without an `auth_token` is never usable — requests send only
 * that key — so it is restored as signed out instead of sending the user to a
 * dashboard that can only bounce them back. Whatever path left the two halves
 * out of step (an older build, another tab, a manual edit), boot heals it.
 */
export function reconcilePersistedAuth<T extends PersistedAuth>(
  persisted: T | undefined,
  rawToken: string | null,
): T | Partial<T> {
  if (!persisted) return {};
  if (persisted.isAuthenticated && !rawToken) {
    return { ...persisted, token: null, user: null, isAuthenticated: false };
  }
  return persisted;
}

/** HTTP status from an error raised by api/client.ts, or undefined. */
export function errorStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as { status_code?: unknown; response?: { status?: unknown } };
  // The response interceptor rejects with ApiError ({ status_code }); a raw
  // AxiosError (requests made outside apiClient) carries response.status.
  if (typeof e.response?.status === 'number') return e.response.status;
  return typeof e.status_code === 'number' ? e.status_code : undefined;
}
